import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { keyBy, groupBy, mapValues, sortBy } from 'lodash-es';
import {
  CountryRegistryView,
  CountryEntryView,
  SubdivisionEntryView,
  ServiceRegionEntryView,
} from '@/api/apis/facilities';
import { NameByValue } from '@/types';
import { LocationLike } from '@/api/types';
import { getCountriesFilterByServiceRegion, WithExtendedLocation, withExtendedLocation } from '@/utils/helpers';
import { NA_REGION_ID } from '@/constants/serviceRegion.constants';
import { useGeoLibraryQuery } from '@/queries/geo-library';

export type CountriesDataContextValue = {
  isLoading: boolean;
  countryCodes: string[];
  countries: CountryEntryView[];
  countriesByIso2: Record<string, CountryEntryView>;
  nonEuCountryCodes: Set<string>;
  subdivisions: Record<string, SubdivisionEntryView[]>;
  subdivisionsByCode: Record<string, SubdivisionEntryView>;
  currencies: CountryRegistryView['currencies'];
  serviceRegions: Record<string, ServiceRegionEntryView>;
  countriesSelectList: NameByValue[];
  composedSubdivisionsList: Record<string, NameByValue[]>;
  resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined;
  extendLocation: <T extends { location: LocationLike }>(item: T) => WithExtendedLocation<T>;
};

const EMPTY_DATA = {
  countryCodes: [],
  countries: [],
  countriesByIso2: {},
  nonEuCountryCodes: new Set<string>(),
  subdivisions: {},
  subdivisionsByCode: {},
  currencies: [],
  serviceRegions: {},
  countriesSelectList: [],
  composedSubdivisionsList: {},
  resolveSubdivisionName: (stateCode) => stateCode ?? undefined,
  extendLocation: (item) => withExtendedLocation(item),
} satisfies Omit<CountriesDataContextValue, 'isLoading'>;

const CountriesDataContext = createContext<CountriesDataContextValue | null>(null);

export const CountriesDataProvider = ({ children }: PropsWithChildren) => {
  const { data, isLoading } = useGeoLibraryQuery();

  const value = useMemo<CountriesDataContextValue>(() => {
    if (!data) return { isLoading, ...EMPTY_DATA };

    const { countries, currencies } = data;

    const countriesByIso2 = keyBy(countries, 'alpha2');
    const subdivisions = groupBy(data.subdivisions, 'countryAlpha2');
    const subdivisionsByCode = keyBy(data.subdivisions, 'code');
    const serviceRegions = keyBy(data.serviceRegions, 'id');
    const countryCodes = countries.map((c) => c.alpha2);
    const nonEuCountriesFilter = getCountriesFilterByServiceRegion(NA_REGION_ID, countriesByIso2);
    const nonEuCountryCodes = new Set(nonEuCountriesFilter ? nonEuCountriesFilter.split(',') : []);

    const countriesSelectList = sortBy(
      countries.map<NameByValue>((c) => ({ value: c.alpha2, name: c.name })),
      'name',
    );
    const composedSubdivisionsList = mapValues(subdivisions, (subs) =>
      subs.map<NameByValue>((s) => ({ value: s.shortCode, name: s.name })),
    );

    const resolveSubdivisionName = (stateCode?: string | null, countryCode?: string | null) => {
      if (!stateCode) return undefined;
      const isFullIsoCode = stateCode.includes('-');
      if (!isFullIsoCode && !countryCode) return stateCode;
      const key = isFullIsoCode ? stateCode : `${countryCode}-${stateCode}`;
      return subdivisionsByCode[key]?.name ?? stateCode;
    };

    const extendLocation = <T extends { location: LocationLike }>(item: T) =>
      withExtendedLocation(item, { countriesByIso2, resolveSubdivisionName });

    return {
      isLoading,
      countryCodes,
      countries,
      countriesByIso2,
      nonEuCountryCodes,
      subdivisions,
      subdivisionsByCode,
      currencies,
      serviceRegions,
      countriesSelectList,
      composedSubdivisionsList,
      resolveSubdivisionName,
      extendLocation,
    };
  }, [data, isLoading]);

  return <CountriesDataContext.Provider value={value}>{children}</CountriesDataContext.Provider>;
};

export const useCountriesData = () => {
  const context = useContext(CountriesDataContext);
  if (context === null) {
    throw new Error('useCountriesData must be used within a CountriesDataProvider');
  }
  return context;
};
